import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useEditorStore } from '~/shell/Store';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { useMediaFetch } from './hooks/useMediaFetch';
import { MenuItem } from './components/LeftMenu';
import LeftMenu from './components/LeftMenu';
import UploadSection from './components/UploadSection';
import UnsplashSection from './sections/UnsplashSection';
import PexelsSection from './sections/PexelsSection';
import AiSection from './sections/AiSection';
import EasyLlamaSection from './sections/EasyLlamaSection';
import MediaGrid from './components/MediaGrid';
import { MediaFile } from './components/MediaGrid';
import Container from './components/Container';
import Wrapper from './components/Wrapper';
import Popup from './components/Popup';
import ImageDownloadMenu, {
  validImageId
} from './components/ImageDownloadMenu';
import styled from 'styled-components';

const CurrentImage = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #eee;
  font-family: Raleway;
  font-size: 13px;
  img {
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 6px;
  }
  span {
    flex: 1;
  }
`;
import {
  buildImageGenerationContext,
  type ImageGenerationSlot
} from '~/data/imageGenerationPolling';

type FilePickerProps = {
  updateFile: (file: MediaFile) => void;
  type: 'image' | 'video' | 'document' | 'audio';
  fileElRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  imageGenerationSlot?: ImageGenerationSlot;
  currentImage?: {
    id: string | number;
    mediaUrl?: string;
    url?: string;
    name?: string;
  } | null;
};

const getInitialMenu = (
  type: 'image' | 'video' | 'document' | 'audio'
): MenuItem[] => {
  if (type === 'document') {
    return [
      { title: 'All files', active: true, className: 'all_files' },
      { title: 'File Upload', active: false, className: 'upload' }
    ];
  } else if (type === 'audio') {
    return [
      { title: 'All audio', active: true, className: 'all_files' },
      { title: 'File Upload', active: false, className: 'upload' }
    ];
  } else if (type === 'image') {
    return [
      { title: 'All images', active: true, className: 'all_images' },
      { title: 'File Upload', active: false, className: 'upload' },
      { title: 'Unsplash', active: false, className: 'unsplash' },
      { title: 'EasyLlama Library', active: false, className: 'easyllama' },
      { title: 'Create with AI', active: false, className: 'ai' }
    ];
  } else {
    return [
      { title: 'All videos', active: true, className: 'all_videos' },
      { title: 'File Upload', active: false, className: 'upload' },
      { title: 'Pexels', active: false, className: 'pexels' }
    ];
  }
};

const FilePickerV2: React.FC<FilePickerProps> = ({
  updateFile,
  type,
  fileElRef,
  onClose,
  currentImage,
  imageGenerationSlot = { placement: 'inline_image' }
}) => {
  const { apiService, sequence } = useEditorStore();
  const imageGenerationContext = buildImageGenerationContext(
    sequence,
    imageGenerationSlot
  );
  const [canWeUpdatePage, setCanWeUpdatePage] = useState(true);
  const [search, setSearch] = useState('');
  const [noResults, setNoResults] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [textForUploading, setTextForUploading] = useState('Uploading');
  const [uploadState, setUploadState] = useState<{
    isUploading: boolean;
    cancelFn?: () => void;
  }>({ isUploading: false });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [files, setFiles] = useState<MediaFile[]>([]);

  const mediaTypes =
    type == 'document'
      ? 'documents'
      : type == 'image'
        ? 'pictures'
        : type == 'video'
          ? 'videos'
          : 'audios';

  const weNeedToLoadRef = useRef<HTMLDivElement | null>(null);
  const isRequestInProgress = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);

  const [menu, setMenu] = useState<MenuItem[]>(getInitialMenu(type));
  const [selectedId, setSelectedId] = useState<number>();
  const activeMenu = menu.find(item => item.active)?.className || '';

  const {
    loading: mediaLoading,
    deleteMedia,
    setFiles: mediaSetFiles
  } = useMediaFetch(
    type,
    activeMenu === 'all_images' ||
      activeMenu === 'all_videos' ||
      activeMenu === 'all_files'
      ? ''
      : 'upload'
  );

  const handleMenuClick = (item: MenuItem) => {
    if (activeMenu !== item.className) {
      setMenu(
        menu.map(menuItem => ({
          ...menuItem,
          active: menuItem.className === item.className
        }))
      );
      setPage(1);
      setFiles([]);
      setSearch('');
      setDebouncedSearch('');
      setCanWeUpdatePage(true);
      setLoading(false);
    }
  };

  const handleSelect = (file: MediaFile) => {
    setSelectedId(file.id);
    updateFile(file);
  };

  const handleDelete = async (id: number) => {
    deleteMedia(id).then(response => {
      if (response.success) {
        setFiles(prevFiles => prevFiles.filter(file => file.id !== id));
      }
    });
  };

  const handleFileUpload = (file: MediaFile) => {
    setFiles(prev => [file, ...prev]);
    setSelectedId(file.id);
  };

  const handleUploadingChange = useCallback(
    (isUploading: boolean, cancelFn?: () => void) => {
      setUploadState({ isUploading, cancelFn });
    },
    []
  );

  const handleClose = () => {
    if (!uploadState.isUploading) {
      onClose();
    } else {
      const shouldCancel = window.confirm(
        'Your file is still uploading. Do you want to cancel the upload and close?'
      );
      if (shouldCancel) {
        uploadState.cancelFn?.();
        onClose();
      }
    }
  };

  const setFilesForMediaType = (res: Record<string, MediaFile[]>) => {
    if (res && res[mediaTypes] && res[mediaTypes].length > 0) {
      setFiles(prevFiles => [...prevFiles, ...res[mediaTypes]]);
      setCanWeUpdatePage(true);
    } else {
      setCanWeUpdatePage(false);
    }
    setLoading(false);
  };

  const setPexelsFiles = (res: Record<string, MediaFile[]>) => {
    if (res && res.videos && res.videos.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...res.videos]);
      setCanWeUpdatePage(true);
    } else {
      setNoResults(true);
      setCanWeUpdatePage(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (canWeUpdatePage && !isRequestInProgress.current) {
      setLoading(true);
      isRequestInProgress.current = true;

      const fetchData = async () => {
        try {
          if (
            (activeMenu == 'upload' || activeMenu == 'easyllama') &&
            apiService &&
            canWeUpdatePage &&
            type !== 'document'
          ) {
            const res = await apiService.getMedia(
              mediaTypes,
              page,
              activeMenu == 'easyllama' ? 'easyllama' : 'uploaded'
            );
            setFilesForMediaType(res);
          }

          if (
            (activeMenu == 'all_images' || activeMenu == 'all_videos') &&
            apiService &&
            canWeUpdatePage &&
            type !== 'document'
          ) {
            const res = await apiService.getMedia(mediaTypes, page, '');
            setFilesForMediaType(res);
          }
        } finally {
          isRequestInProgress.current = false;
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [activeMenu, apiService, page]);

  useEffect(() => {
    if (canWeUpdatePage && !isRequestInProgress.current) {
      setLoading(true);
      isRequestInProgress.current = true;

      const fetchData = async () => {
        try {
          if (
            activeMenu == 'unsplash' &&
            apiService &&
            debouncedSearch &&
            canWeUpdatePage
          ) {
            setNoResults(false);
            const res = await apiService.getUnsplashImages(
              debouncedSearch,
              page
            );
            if (res && res.pictures && res.pictures.length > 0) {
              setFiles(prevFiles => [...prevFiles, ...res.pictures]);
              setCanWeUpdatePage(true);
            } else {
              setNoResults(true);
              setCanWeUpdatePage(false);
            }
          }

          if (
            apiService &&
            activeMenu == 'unsplash' &&
            search == '' &&
            canWeUpdatePage
          ) {
            const res = await apiService.getMedia(mediaTypes, page, 'unsplash');
            if (res && res[mediaTypes] && res[mediaTypes].length > 0) {
              setFiles(prevFiles => [...prevFiles, ...res[mediaTypes]]);
              setCanWeUpdatePage(true);
            } else {
              setCanWeUpdatePage(false);
            }
          }

          if (
            apiService &&
            activeMenu == 'ai' &&
            search == '' &&
            canWeUpdatePage
          ) {
            const res = await apiService.getMedia(mediaTypes, page, 'dalle');
            setFilesForMediaType(res);
          }

          if (activeMenu == 'pexels' && apiService && canWeUpdatePage) {
            setNoResults(false);
            if (debouncedSearch) {
              const res = await apiService.getPexels(debouncedSearch, page);
              setPexelsFiles(res);
            } else {
              const res = await apiService.getMedia(mediaTypes, page, 'pexels');
              setPexelsFiles(res);
            }
          }

          if (
            (activeMenu == 'all_files' ||
              (activeMenu == 'upload' && type == 'document')) &&
            apiService &&
            canWeUpdatePage
          ) {
            const res = await apiService.getMedia(mediaTypes, page, 'upload');
            setFilesForMediaType(res);
          }
        } finally {
          isRequestInProgress.current = false;
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [activeMenu, apiService, debouncedSearch, page]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    const text = activeMenu == 'ai' ? 'Generating' : 'Uploading';

    if (uploadState.isUploading) {
      interval = setInterval(() => {
        setTextForUploading(prev => {
          if (prev === `${text}...`) {
            return text;
          }
          return prev + '.';
        });
      }, 250);
    } else {
      setTextForUploading(text);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [uploadState.isUploading, activeMenu]);

  useEffect(() => {
    setNoResults(false);
    const handler = setTimeout(() => {
      if (search && search.trim() != '') {
        setPage(1);
        setDebouncedSearch(search);
        setFiles([]);
        setCanWeUpdatePage(true);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    const supportsInfiniteScroll =
      activeMenu === 'all_images' ||
      activeMenu === 'all_videos' ||
      activeMenu === 'all_files' ||
      activeMenu === 'easyllama';

    if (!supportsInfiniteScroll) return;

    const attachObserver = () => {
      const popupEl = fileElRef?.current || undefined;
      const imagesRoot = popupEl?.querySelector?.('.images') || null;

      if (!imagesRoot) {
        retryTimeoutRef.current = window.setTimeout(attachObserver, 50);
        return;
      }

      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      observerRef.current = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting && !isRequestInProgress.current) {
              setPage(prevPage => prevPage + 1);
            }
          });
        },
        {
          root: imagesRoot as Element | null,
          rootMargin: '0px',
          threshold: 0.1
        }
      );

      const target = weNeedToLoadRef.current;
      if (target) {
        observerRef.current.observe(target);
      }
    };

    attachObserver();

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [activeMenu, fileElRef]);

  return (
    <Container>
      <Popup ref={fileElRef}>
        <Wrapper>
          <CloseButton type="close" onClick={handleClose} autoFocus={false} />

          <LeftMenu menu={menu} onMenuItemClick={handleMenuClick}>
            {type === 'image' &&
              sequence?.internalSettings &&
              currentImage &&
              validImageId(currentImage.id) && (
                <CurrentImage>
                  <img src={currentImage.mediaUrl || currentImage.url} alt="" />
                  <span>Current image</span>
                  <ImageDownloadMenu imageId={currentImage.id} />
                </CurrentImage>
              )}
          </LeftMenu>

          {activeMenu === 'upload' && (
            <div className="right">
              <UploadSection
                type={type}
                files={files}
                loading={loading}
                selectedId={selectedId}
                onSelect={handleSelect}
                onDelete={handleDelete}
                onFileUpload={handleFileUpload}
                loadMoreRef={weNeedToLoadRef}
                onUploadingChange={handleUploadingChange}
              />
            </div>
          )}
          {(activeMenu === 'all_images' ||
            activeMenu === 'all_videos' ||
            activeMenu === 'all_files') && (
            <div className="right">
              <h3>
                All{' '}
                {type === 'document'
                  ? 'Documents'
                  : type === 'image'
                    ? 'Images'
                    : 'Videos'}
              </h3>

              <MediaGrid
                files={files}
                loading={loading}
                type={type}
                variant="long"
                selectedId={selectedId}
                onSelect={handleSelect}
                onDelete={handleDelete}
                loadMoreRef={weNeedToLoadRef}
                showNoResults
              />
            </div>
          )}
          {activeMenu === 'unsplash' && (
            <div className="right">
              <UnsplashSection onSelect={handleSelect} />
            </div>
          )}
          {activeMenu === 'pexels' && (
            <div className="right">
              <PexelsSection onSelect={handleSelect} onDelete={handleDelete} />
            </div>
          )}
          {activeMenu === 'ai' && (
            <div className="right">
              <AiSection
                files={files}
                onSelect={handleSelect}
                context={imageGenerationContext}
              />
            </div>
          )}
          {activeMenu === 'easyllama' && (
            <div className="right">
              <EasyLlamaSection onSelect={handleSelect} />
            </div>
          )}
        </Wrapper>
      </Popup>
    </Container>
  );
};

export default FilePickerV2;
